import React, { useState, useEffect, useRef, useCallback } from "react";
import editorange from "assets/images/icon/edit-orange.png";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getCompany,
    updateCompany,
    getYtdEntries,
    createYtdEntry,
    getCompanyDocuments,
    uploadCompanyDocument,
    deleteCompanyDocument,
} from "services/company";
import Input from "components/Input";

const ConfirmModal = ({ message, onConfirm, onCancel }) => (
    <div style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center"
    }}>
        <div style={{
            background: "#fff", borderRadius: 12, padding: "28px 32px",
            minWidth: 320, maxWidth: 420, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            textAlign: "center"
        }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🗑️</div>
            <h5 style={{ marginBottom: 8, fontWeight: 600 }}>Delete File</h5>
            <p style={{ color: "#555", marginBottom: 24, fontSize: 14 }}>{message}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
                <button
                    className="btn btn-outline-secondary btn-sm"
                    style={{ minWidth: 90 }}
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-danger btn-sm"
                    style={{ minWidth: 90 }}
                    onClick={onConfirm}
                >
                    Delete
                </button>
            </div>
        </div>
    </div>
);

const EMPTY_YTD = {
    firstName: "",
    lastName: "",
    ssn: "",
    grossWages: "",
    federalWithholding: "",
    stateWithholding: "",
};

const Field = ({ label, value }) => (
    <div>
        <p className="m-0 title">{label}</p>
        <p className="m-0 sub-titles">{value || "—"}</p>
    </div>
);

const PriorPayrollSection = ({ startInEditMode = false } = {}) => {
    const [isEditing, setIsEditing]   = useState(!!startInEditMode);
    const [form, setForm]             = useState({ previousProvider: "", firstPayrollDate: "" });
    const [ytdForm, setYtdForm]       = useState(EMPTY_YTD);
    const [showYtdForm, setShowYtdForm] = useState(false);
    const [uploading, setUploading]   = useState(false);
    const [deletingId, setDeletingId]     = useState(null);
    const [confirmDocId, setConfirmDocId] = useState(null);
    const [confirmDocName, setConfirmDocName] = useState("");
    const fileRef                     = useRef(null);
    const queryClient                 = useQueryClient();

    const { data: company } = useQuery({ queryKey: ["company"], queryFn: getCompany });
    const { data: ytdEntries = [] } = useQuery({
        queryKey: ["ytdEntries"],
        queryFn: getYtdEntries,
    });
    const { data: documents = [] } = useQuery({
        queryKey: ["companyDocuments", "payroll_summary"],
        queryFn: () => getCompanyDocuments("payroll_summary"),
    });

    useEffect(() => {
        if (company) {
            setForm({
                previousProvider: company.previousProvider || "",
                firstPayrollDate: company.firstPayrollDate
                    ? company.firstPayrollDate.substring(0, 10)
                    : "",
            });
        }
    }, [company]);

    const updateMutation = useMutation({
        mutationFn: updateCompany,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["company"] });
            setIsEditing(false);
        },
    });

    const ytdMutation = useMutation({
        mutationFn: createYtdEntry,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["ytdEntries"] });
            setYtdForm(EMPTY_YTD);
            setShowYtdForm(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleYtdChange = (e) => {
        const { name, value } = e.target;
        setYtdForm((p) => ({ ...p, [name]: value }));
    };

    const handleSave = () => updateMutation.mutate(form);

    const handleYtdSubmit = (e) => {
        e.preventDefault();
        ytdMutation.mutate(ytdForm);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("documentType", "payroll_summary");
            await uploadCompanyDocument(fd);
            queryClient.invalidateQueries({ queryKey: ["companyDocuments"] });
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const handleDeleteDocument = (docId, docName) => {
        setConfirmDocId(docId);
        setConfirmDocName(docName);
    };

    const confirmDelete = useCallback(async () => {
        const docId = confirmDocId;
        setConfirmDocId(null);
        setDeletingId(docId);
        try {
            await deleteCompanyDocument(docId);
            queryClient.invalidateQueries({ queryKey: ["companyDocuments"] });
        } catch (err) {
            console.error("Delete failed", err);
        } finally {
            setDeletingId(null);
        }
    }, [confirmDocId, queryClient]);

    return (
        <>
        <div className="general-info">
            {/* ── Prior Payroll Provider ───────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Prior Payroll</h5>
                {!isEditing && (
                    <button
                        className="btn btn-link p-0"
                        onClick={() => setIsEditing(true)}
                    >
                        <img src={editorange} alt="Edit" width={20} />
                    </button>
                )}
            </div>

            {!isEditing ? (
                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <Field label="Previous Payroll Provider" value={form.previousProvider} />
                    </div>
                    <div className="col-md-6">
                        <Field label="First Payroll Date with Payroll Noot" value={form.firstPayrollDate} />
                    </div>
                </div>
            ) : (
                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <Input
                            label="Previous Payroll Provider"
                            name="previousProvider"
                            value={form.previousProvider}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-md-6">
                        <Input
                            label="First Payroll Date with Payroll Noot"
                            name="firstPayrollDate"
                            type="date"
                            value={form.firstPayrollDate}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-12 d-flex gap-2">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSave}
                            disabled={updateMutation.isPending}
                        >
                            {updateMutation.isPending ? "Saving…" : "Save"}
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setIsEditing(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <hr />

            {/* ── Payroll Summary Documents ───────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Payroll Summary Files</h6>
                <div>
                    <input
                        type="file"
                        ref={fileRef}
                        className="d-none"
                        onChange={handleFileUpload}
                        accept=".pdf,.csv,.xlsx,.xls"
                    />
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                    >
                        {uploading ? "Uploading…" : "+ Upload File"}
                    </button>
                </div>
            </div>

            {documents.length === 0 ? (
                <p className="text-muted small">No files uploaded yet.</p>
            ) : (
                <table className="table table-sm table-hover mb-4">
                    <thead>
                        <tr>
                            <th>File Name</th>
                            <th>Size</th>
                            <th>Uploaded</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {documents.map((doc) => (
                            <tr key={doc.id}>
                                <td>{doc.fileName}</td>
                                <td>{doc.fileSize ? `${Math.round(doc.fileSize / 1024)} KB` : "—"}</td>
                                <td>
                                    {doc.uploadedAt
                                        ? new Date(doc.uploadedAt).toLocaleDateString()
                                        : "—"}
                                </td>
                                <td className="d-flex gap-2 align-items-center">
                                    {doc.fileUrl && (
                                        <a
                                            href={doc.fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-link btn-sm p-0"
                                        >
                                            Download
                                        </a>
                                    )}
                                    <button
                                        className="btn btn-link btn-sm p-0 text-danger"
                                        onClick={() => handleDeleteDocument(doc.id, doc.fileName)}
                                        disabled={deletingId === doc.id}
                                        title="Delete file"
                                    >
                                        {deletingId === doc.id ? "…" : "Delete"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <hr />

            {/* ── YTD Entries ─────────────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Employee YTD Payroll Entries</h6>
                <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setShowYtdForm((p) => !p)}
                >
                    {showYtdForm ? "Cancel" : "+ Add Entry"}
                </button>
            </div>

            {showYtdForm && (
                <form onSubmit={handleYtdSubmit} className="row g-2 mb-3 p-3 border rounded bg-light">
                    <div className="col-md-3">
                        <Input
                            label="First Name"
                            name="firstName"
                            value={ytdForm.firstName}
                            onChange={handleYtdChange}
                            required
                        />
                    </div>
                    <div className="col-md-3">
                        <Input
                            label="Last Name"
                            name="lastName"
                            value={ytdForm.lastName}
                            onChange={handleYtdChange}
                            required
                        />
                    </div>
                    <div className="col-md-2">
                        <Input
                            label="SSN"
                            name="ssn"
                            value={ytdForm.ssn}
                            onChange={handleYtdChange}
                            placeholder="XXX-XX-XXXX"
                        />
                    </div>
                    <div className="col-md-2">
                        <Input
                            label="Gross Wages"
                            name="grossWages"
                            value={ytdForm.grossWages}
                            onChange={handleYtdChange}
                        />
                    </div>
                    <div className="col-md-1">
                        <Input
                            label="Fed W/H"
                            name="federalWithholding"
                            value={ytdForm.federalWithholding}
                            onChange={handleYtdChange}
                        />
                    </div>
                    <div className="col-md-1">
                        <Input
                            label="State W/H"
                            name="stateWithholding"
                            value={ytdForm.stateWithholding}
                            onChange={handleYtdChange}
                        />
                    </div>
                    <div className="col-12">
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={ytdMutation.isPending}
                        >
                            {ytdMutation.isPending ? "Saving…" : "Save Entry"}
                        </button>
                    </div>
                </form>
            )}

            {ytdEntries.length === 0 ? (
                <p className="text-muted small">No YTD entries yet.</p>
            ) : (
                <div className="table-responsive">
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>SSN</th>
                                <th>Gross Wages</th>
                                <th>Fed W/H</th>
                                <th>State W/H</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ytdEntries.map((e) => (
                                <tr key={e.id}>
                                    <td>{e.firstName}</td>
                                    <td>{e.lastName}</td>
                                    <td>{e.ssn || "—"}</td>
                                    <td>{e.grossWages ? `$${Number(e.grossWages).toLocaleString()}` : "—"}</td>
                                    <td>{e.federalWithholding ? `$${Number(e.federalWithholding).toLocaleString()}` : "—"}</td>
                                    <td>{e.stateWithholding ? `$${Number(e.stateWithholding).toLocaleString()}` : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>

        {confirmDocId && (
            <ConfirmModal
                message={`Are you sure you want to delete "${confirmDocName}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => setConfirmDocId(null)}
            />
        )}
        </>
    );
};

export default PriorPayrollSection;
